<template>
  <v-dialog v-model="isOpen" max-width="558">
    <v-card>
      <v-card-title class="sequence-info-dialog__title">Sequence Info</v-card-title>
      <v-card-text class="sequence-info-dialog__content">
        <div class="sequence-info-dialog__row">
          <span class="sequence-info-dialog__label">Name</span>
          <span>{{ sequence?.name || '-' }}</span>
        </div>
        <div class="sequence-info-dialog__row">
          <span class="sequence-info-dialog__label">Frames</span>
          <span>{{ sequence?.no_of_images ?? '-' }}</span>
        </div>
        <div class="sequence-info-dialog__row">
          <span class="sequence-info-dialog__label">Date</span>
          <span>{{ formatDate(sequence?.date) }}</span>
        </div>
        <div class="sequence-info-dialog__row">
          <span class="sequence-info-dialog__label">UUID</span>
          <span class="sequence-info-dialog__value">{{ sequence?.uuid || '-' }}</span>
        </div>
        <h3 class="sequence-info-dialog__section-title">Annotation Sets</h3>
        <div v-if="annotationSets.length > 0" class="sequence-info-dialog__annotation-set-list">
          <div
            v-for="annotationSet in annotationSets"
            :key="annotationSet.id"
            class="sequence-info-dialog__annotation-set-row"
          >
            <span>{{ annotationSet.name || `Annotation Set ${annotationSet.id}` }}</span>
            <SVGIcon
              :iconName="'info'"
              :width="'20px'"
              :height="'20px'"
              title="Annotation Set Info"
              @click="openAnnotationSetInfo(annotationSet.id)"
            />
          </div>
        </div>
        <div v-else class="sequence-info-dialog__empty">No annotation sets</div>
        <AnnotationSetInfoModal
          v-if="selectedAnnotationSet"
          v-model="showAnnotationSetInfo"
          :annotationSet="selectedAnnotationSet"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" class="button" @click="isOpen = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import AnnotationSetInfoModal from '@/components/DatasetComponent/DatasetManagement/AnnotationSetInfoModal.vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  sequence: {
    type: Object,
    default: () => ({}),
  },
  annotationSets: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);
const selectedAnnotationSetId = ref(null);
const showAnnotationSetInfo = ref(false);

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

function formatDate(date) {
  if (!date) return '-';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const selectedAnnotationSet = computed(() => props.annotationSets.find(
  (annotationSet) => String(annotationSet.id) === String(selectedAnnotationSetId.value),
) || null);

function openAnnotationSetInfo(id) {
  selectedAnnotationSetId.value = id;
  showAnnotationSetInfo.value = true;
}
</script>

<style lang="scss" scoped>
.sequence-info-dialog__title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.sequence-info-dialog__content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sequence-info-dialog__row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 12px;
  align-items: center;
}

.sequence-info-dialog__label {
  font-weight: 600;
}

.sequence-info-dialog__value {
  overflow-wrap: anywhere;
}

.sequence-info-dialog__section-title {
  margin: 12px 0 0;
}

.sequence-info-dialog__annotation-set-list {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sequence-info-dialog__annotation-set-row {
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  width: 100%;
}

.sequence-info-dialog__empty {
  color: var(--body-text-color-secondary);
}
</style>
